import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";

import {
  createBusbarSelectionRun,
  type BusbarSelectionRunCreateRequest,
  type BusbarSelectionRunResponse,
} from "../services/busbarSelection";

/**
 * Busbar selection study mutation (EOS-07c).
 *
 * Mirrors useBusbarEvidence:
 * - One in-flight request at a time: a new submit aborts the previous one.
 * - The in-flight request is aborted on unmount so no state update lands on an unmounted
 *   component.
 * - The run is attached to the revision chosen in the top bar, read at submit time: it goes
 *   into the run request as project_revision_id. With no project, or one without an open
 *   revision, the field is left out and the run is unassigned.
 * - Every calculation is persisted as a run, and the run with its result and the data set pin
 *   is exposed exactly as parsed, including a NO_SOLUTION result - a selection no arrangement
 *   or catalogue row satisfies is engineering evidence too.
 * - A linked fault run must belong to the revision the study is saved in; the backend says so
 *   (409) and its message reaches the page unchanged, as do the rules of the engine (422).
 *
 * The form never puts a project revision in the request; this hook does.
 */
export function useBusbarSelection() {
  const controllerRef = useRef<AbortController | null>(null);
  const { activeRevisionId } = useProject();

  const mutation = useMutation<
    BusbarSelectionRunResponse,
    Error,
    BusbarSelectionRunCreateRequest
  >({
    mutationKey: ["electrical", "busbar-selection", "runs", "create"],
    mutationFn: (payload) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const projectRevisionId = activeRevisionId();
      return createBusbarSelectionRun(
        projectRevisionId === null
          ? payload
          : { ...payload, project_revision_id: projectRevisionId },
        controller.signal,
      );
    },
  });

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const calculate = useCallback(
    (payload: BusbarSelectionRunCreateRequest) => mutation.mutateAsync(payload),
    [mutation],
  );

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    calculate,
    reset,
    response: mutation.data ?? null,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export type UseBusbarSelectionResult = ReturnType<typeof useBusbarSelection>;
